import Vue from 'vue'
import Vuex from 'vuex'
import { db, auth } from '@/db'
import { vuexfireMutations, firestoreAction } from 'vuexfire'
import { VuexPersistence } from 'vuex-persist'

Vue.use(Vuex)

const CHECK_DELAY = 10

const vuexLocal = new VuexPersistence({
  storage: window.localStorage
})

export default new Vuex.Store({
  state: {
    authUser: null,
    currentQuery: null,
    list: [],
    categories: [],
    appMenuOpen: false,
    checkedItemsVisible: false,
    editing: false,
    draggingCategories: false
  },
  mutations: {
    ...vuexfireMutations,
    USER_SIGNED_IN (state, user) {
      if (!state.authUser) state.authUser = user
    },
    USER_SIGNED_OUT (state) {
      state.authUser = false
    },
    SET_CURRENT_QUERY (state, string) {
      state.currentQuery = string
    },
    SET_CURRENT_LIST (state, array) {
      state.currentList = array
    },
    SET_APP_MENU_OPEN (state, open) {
      state.appMenuOpen = open
    },
    SET_CHECKED_ITEMS_VISIBILITY (state, visible) {
      state.checkedItemsVisible = visible
    },
    SET_ITEM_INDEX (state, {id, index}) {
      state.list.find(i => i.id === id).index = index
    },
    SET_EDITING_MODE (state, force) {
      state.editing = force
    },
    SET_DRAGGING_CATEGORIES (state, force) {
      state.draggingCategories = force
    }
  },
  actions: {
    bindList: firestoreAction(({state, bindFirestoreRef}) =>
      bindFirestoreRef('list', db
        .doc(`users/${state.authUser.uid}`)
        .collection('list')
        .orderBy('index')
      )
    ),
    bindCategories: firestoreAction(({state, bindFirestoreRef}) =>
      bindFirestoreRef('categories', db
        .doc(`users/${state.authUser.uid}`)
        .collection('categories')
        .orderBy('index')
      )
    ),

    userSignIn ({commit}, {email, password}) {
      auth.signInWithEmailAndPassword(email, password)
        .then(() => {
          commit('USER_SIGNED_IN', auth.currentUser)
        })
    },
    userSignUp ({commit}, {email, password}) {
      auth.createUserWithEmailAndPassword(email, password)
        .then(() => {
          commit('USER_SIGNED_IN', auth.currentUser)
          return true
        })
        .catch(() => {
          return false
        })
    },
    userSignOut ({commit}) {
      auth.signOut().then(() => {
        commit('USER_SIGNED_OUT')
        commit('SET_APP_MENU_OPEN', false)
      })
    },

    setListItemChecked ({state}, {id, checked}) {
      setTimeout(() => {
        db.doc(`users/${state.authUser.uid}/list/${id}`).update({checked: checked})
      }, CHECK_DELAY)
    },
    setListItemCount ({state}, {id, count}) {
      db.doc(`users/${state.authUser.uid}/list/${id}`).update({count: count})
    },
    setListItemName ({state}, {id, name}) {
      db.doc(`users/${state.authUser.uid}/list/${id}`).update({name: name})
    },
    setListItemIndex ({state}, {id, index}) {
      db.doc(`users/${state.authUser.uid}/list/${id}`).update({index: index})
    },
    swapListItemsIndexes ({state, commit}, {id1, id2}) {
      const index1 = state.list.find(i => i.id === id1).index
      const index2 = state.list.find(i => i.id === id2).index

      db.doc(`users/${state.authUser.uid}/list/${id1}`).update({index: index2})
      db.doc(`users/${state.authUser.uid}/list/${id2}`).update({index: index1})
    },
    setListItemCategory ({state}, {id, categoryID}) {
      const categoryRef = db.doc(`users/${state.authUser.uid}/categories/${categoryID}`)
      db.doc(`users/${state.authUser.uid}/list/${id}`).update({
        categoryRef,
        categoryID
      })
    },
    deleteListItem ({state}, id) {
      db.doc(`users/${state.authUser.uid}/list/${id}`).delete()
    },
    setListItemImportant ({state}, {id, force}) {
      db.doc(`users/${state.authUser.uid}/list/${id}`).update({important: force})
    },

    async createCategoryIfDoesntExist ({state, getters}, name) {
      const existingCategory = state.categories.find(c => c.name === name)
      if (existingCategory) return existingCategory.id

      let result = await db.collection(`users/${state.authUser.uid}/categories`).add({
        name: name,
        index: (getters.maxCategoryIndex || 0) + 1
      })
      return result.id
    },
    setCategoryName ({state}, {id, name}) {
      db.doc(`users/${state.authUser.uid}/categories/${id}`).update({name: name})
    },
    swapCategoryIndexes ({state, commit}, {id1, id2}) {
      const index1 = state.categories.find(i => i.id === id1).index
      const index2 = state.categories.find(i => i.id === id2).index

      db.doc(`users/${state.authUser.uid}/categories/${id1}`).update({index: index2})
      db.doc(`users/${state.authUser.uid}/categories/${id2}`).update({index: index1})
    },
    setDraggingCategories ({commit}, force) {
      commit('SET_DRAGGING_CATEGORIES', force)
    },
    toggleDraggingCategories ({state, commit}) {
      commit('SET_DRAGGING_CATEGORIES', !state.draggingCategories)
    },

    setCurrentQuery ({commit}, string) {
      commit('SET_CURRENT_QUERY', string)
    },
    setAppMenuOpen ({commit}, open) {
      commit('SET_APP_MENU_OPEN', open)
    },
    openAppMenu ({dispatch}) {
      dispatch('setAppMenuOpen', true)
    },
    closeAppMenu ({dispatch}) {
      dispatch('setAppMenuOpen', false)
    },
    toggleAppMenu ({state, dispatch}) {
      dispatch('setAppMenuOpen', !state.appMenuOpen)
    },
    toggleCheckedVisibility ({state, commit}) {
      commit('SET_CHECKED_ITEMS_VISIBILITY', !state.checkedItemsVisible)
    },
    toggleEditingMode ({state, commit}) {
      commit('SET_EDITING_MODE', !state.editing)
      commit('SET_DRAGGING_CATEGORIES', false)
    }
  },
  getters: {
    userDoc: state => state.authUser && db.doc(`users/${state.authUser.uid}`),
    dbListRef: state => state.authUser && db.doc(`users/${state.authUser.uid}`).collection('list'),
    dbCategoriesRef: state => state.authUser && db.doc(`users/${state.authUser.uid}`).collection('categoires'),
    queriedList: state => state.currentQuery ? state.list.filter(i => i.name.toLowerCase().indexOf(state.currentQuery.toLowerCase()) !== -1) : state.list,
    maxItemIndex: state => Math.max(...state.list.map(o => o.index), 0),
    maxCategoryIndex: state => Math.max(...state.categories.map(o => o.index), 0),
  },
  plugins: [vuexLocal.plugin]
})
